import { For, Show, onMount } from "solid-js";
import { Modal } from "../../components/Modal.tsx";
import { SourceTag } from "../../components/SourceTag.tsx";
import { useI18n } from "../../i18n/provider.tsx";
import { formatScore } from "../../lib/format.ts";
import { commentOf, explanationOf, type AnswerDetail, type QuestionScope } from "./compare.ts";
import type { Question } from "../../api/domain.ts";
import styles from "./QuestionDialog.module.scss";

export type QuestionDialogProps = {
  /** Nothing renders while this is undefined. */
  scope: QuestionScope | undefined;
  /** The side whose value the reader clicked; it gets the focus and the frame. */
  side: "a" | "b";
  nameA: string;
  nameB: string;
  onClose: () => void;
};

/**
 * One question, opened from the comparison: what was asked, how each of the
 * two products answered, and the reasoning the author wrote down.
 *
 * Both sides are always shown. The reader clicked one value, but the question
 * "why is this a 4" is only answered next to the 2 it is being compared with,
 * so the side they asked about is framed and focused rather than shown alone.
 */
export const QuestionDialog = (props: QuestionDialogProps) => {
  const i18n = useI18n();

  const title = () => props.scope?.question?.title ?? props.scope?.capability?.label ?? "";

  return (
    <Modal
      open={props.scope !== undefined}
      title={title()}
      size="wide"
      dismissible
      onClose={props.onClose}
    >
      <Show when={props.scope}>
        {(scope) => (
          <div class={styles.dialog}>
            <div class={styles.about}>
              <Show when={scope().question?.body}>
                {(body) => <p class={styles.body}>{body()}</p>}
              </Show>
              <p class={styles.place}>
                {i18n.t().compare.inCapability}{" "}
                <strong>{scope().capability?.label}</strong>
                <Show when={scope().topic}>
                  {(topic) => (
                    <>
                      <span class={styles.dot} aria-hidden="true">
                        ·
                      </span>
                      {topic().title}
                    </>
                  )}
                </Show>
              </p>
            </div>

            <div class={styles.sides}>
              <Side
                side="a"
                name={props.nameA}
                detail={scope().a}
                question={scope().question}
                focused={props.side === "a"}
              />
              <Side
                side="b"
                name={props.nameB}
                detail={scope().b}
                question={scope().question}
                focused={props.side === "b"}
              />
            </div>
          </div>
        )}
      </Show>
    </Modal>
  );
};

/**
 * How the value was actually given, for the answer types where the number is a
 * translation: a yes or no, or the option the author picked. A rating needs no
 * second reading, and a free text never reaches this dialog.
 */
const givenAs = (question: Question | undefined, detail: AnswerDetail | null, yes: string, no: string): string[] => {
  const value = detail?.record?.value;
  if (value === undefined || value === null || question === undefined) return [];

  const options = "options" in question.answer_type ? question.answer_type.options : [];
  const labelOf = (slug: string) => options.find((option) => option.slug === slug)?.label ?? slug;

  switch (value.kind) {
    case "bool":
      return [value.value ? yes : no];
    case "select":
      return [labelOf(value.value)];
    case "multi_select":
      return value.value.map(labelOf);
    default:
      return [];
  }
};

/** One product's answer: the number, how it was given, who gave it, and why. */
const Side = (props: {
  side: "a" | "b";
  name: string;
  detail: AnswerDetail | null;
  question: Question | undefined;
  focused: boolean;
}) => {
  const i18n = useI18n();
  let column: HTMLElement | undefined;

  /*
   * The side the reader clicked takes the focus before the dialog can claim it
   * for its card, so a screen reader starts on the answer that was asked about.
   */
  onMount(() => {
    if (props.focused) column?.focus();
  });

  const given = () =>
    givenAs(props.question, props.detail, i18n.t().common.yes, i18n.t().common.no);
  const explanation = () => explanationOf(props.detail);
  const comment = () => commentOf(props.detail);

  return (
    <section
      ref={column}
      class={`${styles.side} ${props.side === "a" ? styles.sideA : styles.sideB} ${
        props.focused ? styles.focused : ""
      }`}
      tabIndex={-1}
      aria-label={props.name}
    >
      <h3 class={styles.name}>
        <span class={styles.swatch} aria-hidden="true" />
        {props.name}
      </h3>

      <Show
        when={props.detail}
        fallback={<p class={styles.none}>{i18n.t().compare.noAnswer}</p>}
      >
        {(detail) => (
          <>
            <p class={styles.value}>
              <span class={styles.number}>
                {formatScore(detail().resolved.value, i18n.locale())}
              </span>
              <Show when={given().length > 0}>
                <span class={styles.given}>
                  <span class={styles.givenLabel}>{i18n.t().compare.answeredAs}</span>
                  <For each={given()}>{(label) => <span class={styles.option}>{label}</span>}</For>
                </span>
              </Show>
            </p>

            <p class={styles.origin}>
              <SourceTag source={detail().resolved.source} confidence={detail().resolved.confidence} />
              <span class={styles.author}>{detail().resolved.author}</span>
            </p>

            <div class={styles.reasoning}>
              <h4 class={styles.label}>{i18n.t().compare.explanation}</h4>
              <Show
                when={explanation()}
                fallback={<p class={styles.none}>{i18n.t().compare.noExplanation}</p>}
              >
                {(text) => <p class={styles.text}>{text()}</p>}
              </Show>
            </div>

            <Show when={comment()}>
              {(text) => (
                <div class={styles.reasoning}>
                  <h4 class={styles.label}>{i18n.t().compare.comment}</h4>
                  <p class={`${styles.text} ${styles.remark}`}>{text()}</p>
                </div>
              )}
            </Show>
          </>
        )}
      </Show>
    </section>
  );
};
